import { C } from '@parsina/framework/tokens';
import { LTR_TEXT } from '../i18n';

/**
 * One money fact, full width.
 *
 * The desktop answers "what does this invoice owe" with a 4-up KPI grid. On a
 * phone that grid is the bug: a quarter of 320px cannot hold «۱۲٬۴۵۰٬۰۰۰», so
 * either the figure wraps mid-number or the row pushes the page into
 * horizontal scroll. A large number needs the full width of the screen, and
 * stacking these rows is what a waterfall (subtotal → discount → insurer →
 * patient share → paid → outstanding) wants anyway: it reads top to bottom.
 *
 * The amount is an LTR island. Persian text runs right-to-left and a number is
 * a left-to-right run inside it; without the isolation a trailing unit or a
 * minus sign is dragged to the wrong end of the figure.
 */
export default function MobileMoneyRow({
  label,
  value,
  /** «ت», «٪», «مورد» — whatever the figure is counted in. */
  unit,
  /** `null` (default), 'good', 'warn', 'danger', 'muted' */
  tone = null,
  /** Renders the row as the section's headline. */
  strong = false,
  /** Struck through, for a voided or superseded figure. */
  struck = false,
  hint,
  style,
  'data-testid': testId,
}) {
  const color = {
    good: C.green,
    warn: C.amber,
    danger: C.red,
    muted: C.textMuted,
  }[tone] || (strong ? C.textPrimary : C.textSecondary);

  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
        gap: '0.75rem', minHeight: '1.75rem',
        ...style,
      }}
    >
      <span style={{ minWidth: 0, flexShrink: 1 }}>
        <span style={{
          display: 'block',
          fontSize: strong ? '0.8125rem' : '0.75rem',
          fontWeight: strong ? 700 : 500,
          color: C.textSecondary,
        }}>
          {label}
        </span>
        {hint ? (
          <span style={{ display: 'block', fontSize: '0.6875rem', color: C.textMuted }}>{hint}</span>
        ) : null}
      </span>

      <span
        {...LTR_TEXT}
        style={{
          flexShrink: 0,
          fontSize: strong ? '1.125rem' : '0.875rem',
          fontWeight: strong ? 800 : 700,
          color,
          fontVariantNumeric: 'tabular-nums',
          textDecoration: struck ? 'line-through' : undefined,
        }}
      >
        {value}
        {unit ? <span style={{ fontSize: '0.6875rem', fontWeight: 500, marginInlineStart: '0.25rem', color: C.textMuted }}>{unit}</span> : null}
      </span>
    </div>
  );
}
